/* eslint-disable no-shadow */
/* eslint-disable react/require-default-props */
import React, { useContext, useEffect, useState } from 'react';
import { RetainerSummaryReportTable } from '@nile/reports/src/modules/RetinerReport/components/tables';
import { ContractsUtilizationChart } from '@nile/reports/src/modules/RetinerReport/components/charts';
import DetailedConsumptionReport from '@nile/reports/src/modules/RetinerReport/components/pages/DetailedConsumptionReport';
import { useParams } from 'react-router-dom';
import {
  withErrorBoundary,
  ALL_TABLES,
  useSuperListHook
} from '@nile/components';
import ReportsContext from '@nile/reports/src/modules/home/contexts/Reports';
import { cloneDeep } from 'lodash';

function RetainerReportSheet({
  data,
  sharedReport,
  clientId: initialClientId,
  contract: initialContract,
  allowedContractIds,
  allowedDateRange
}: {
  data: any;
  sharedReport: boolean;
  clientId: string;
  contract?: {
    contractId: string | null;
    contractName?: string;
    contractType?: string | null;
  };
  allowedContractIds?: string[];
  allowedDateRange?: { start: string; end: string };
}) {
  const reportParams = useParams();

  const reportsContext: any = useContext<any>(ReportsContext);

  // const clientId = params.get('clientId');
  // const contractId = params.get('contractId') || undefined;

  const [clientId, setClientId] = useState<string | null>(
    initialClientId ?? null
  );
  const [contract, setContract] = useState<
    | {
        contractId: string | null;
        contractName?: string;
        contractType?: string | null;
      }
    | undefined
  >(initialContract ?? undefined);

  const activeReportFilter =
    reportsContext?.reportCondition &&
    reportsContext?.reportCondition[reportsContext.activeView];

  const { setRecords: setRetainerSummaryReportRecords } = useSuperListHook({
    tableName: ALL_TABLES.retainerSummaryReportTable,
    viewId: reportParams?.id
  });

  function handleClientClick(
    client: { accountId: string; name?: string },
    contract?: {
      contractId: string | null;
      contractName?: string;
      contractType?: string | null;
    }
  ) {
    if (!client?.accountId) return;
    try {
      setClientId(client.accountId);
      if (contract?.contractId) {
        setContract(contract);
      } else {
        setContract(undefined);
      }
      const clientObj = {
        attribute: 'client',
        operator: 'is',
        value: client?.accountId,
        ...(client?.name && { label: client?.name })
      };

      if (activeReportFilter) {
        const updatedCondition = cloneDeep(activeReportFilter || []);
        const existingIndex = updatedCondition.findIndex(
          (cond: any) => cond.attribute === 'client'
        );
        if (existingIndex >= 0) {
          updatedCondition[existingIndex] = clientObj;
          if (updatedCondition[existingIndex]?.value !== clientObj?.value) {
            reportsContext.setReportCondition({
              ...reportsContext.reportCondition,
              [reportsContext.activeView]: updatedCondition
            });
          }
        } else {
          reportsContext.setReportCondition({
            ...reportsContext.reportCondition,
            [reportsContext.activeView]: [...updatedCondition, clientObj]
          });
        }
      } else {
        reportsContext.setReportCondition({
          ...reportsContext.reportCondition,
          [reportsContext.activeView]: [clientObj]
        });
      }
      setRetainerSummaryReportRecords([]);
    } catch (error) {
      console.error(error);
    }
  }

  function resetReportClientFilter() {
    const updatedCondition = cloneDeep(activeReportFilter || []);
    const existingIndex = updatedCondition.findIndex(
      (cond: any) => cond.attribute === 'client'
    );
    if (existingIndex >= 0) {
      updatedCondition.splice(existingIndex, 1);
      reportsContext.setReportCondition({
        ...reportsContext.reportCondition,
        [reportsContext.activeView]: updatedCondition
      });
    }
    setClientId(null);
    setContract(undefined);
  }

  useEffect(() => {
    if (clientId && !contract?.contractId) {
      handleClientClick({ accountId: clientId });
    } else if (clientId && contract?.contractId) {
      handleClientClick({ accountId: clientId }, contract);
    }
  }, [clientId, contract]);

  useEffect(() => {
    if (activeReportFilter) {
      const clientObj = activeReportFilter.find(
        (cond: any) => cond.attribute === 'client'
      );
      if (clientObj?.value !== clientId) {
        setClientId(clientObj?.value ?? null);
        setContract(undefined);
      }
    } else {
      if (clientId) {
        setClientId(initialClientId ?? null);
      }
      if (contract?.contractId) {
        setContract(initialContract ?? undefined);
      }
    }
  }, [activeReportFilter]);

  if (!!clientId || !!(clientId && contract?.contractId)) {
    return (
      <div className="full-width full-height scroll">
        <DetailedConsumptionReport
          clientId={clientId}
          contract={contract}
          sharedReport={sharedReport}
          allowedContractIds={allowedContractIds}
          allowedDateRange={allowedDateRange}
          reportId={data?.id || reportParams?.id}
          handleClientClick={handleClientClick}
          resetReportClientFilter={resetReportClientFilter}
        />
      </div>
    );
  }

  return (
    <div className="full-width full-height scroll">
      <div className="m10 p20 rounded-4px white-to-grey-bg bshadow">
        <ContractsUtilizationChart data={data} />
      </div>
      <div className="m10 rounded-4px white-to-grey-bg bshadow">
        <RetainerSummaryReportTable
          reportId={reportParams?.id}
          handleClientClick={handleClientClick}
        />
      </div>
    </div>
  );
}

export default withErrorBoundary(RetainerReportSheet);

const SharedRetainerReportSheet = ({ data }: { data: any }) => {
  const clientId = data?.data?.operands?.find(
    (cond: any) => cond.attribute === 'client'
  )?.value;
  const contractIds = data?.data?.operands?.find(
    (cond: any) => cond.attribute === 'contractId'
  )?.value;

  return (
    <div className="full-width full-height scroll">
      <RetainerReportSheet
        data={data}
        clientId={clientId}
        contract={
          contractIds?.[0]
            ? {
                contractId: contractIds?.[0]
              }
            : undefined
        }
        allowedContractIds={contractIds || []}
        allowedDateRange={{
          start: data?.startDateTime,
          end: data?.endDateTime
        }}
        sharedReport
      />
    </div>
  );
};

export { SharedRetainerReportSheet };
